import {AfterViewInit, Component, EventEmitter, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import lodash from "lodash";
import {sendQuery} from "../../common/utils";
import {
  DateTimeRange,
  GetCommercialReleaseStats,
  GetReleaseToPartyStats
} from "@portbase/bezoekschip-service-typescriptmodels";
import moment, {unitOfTime} from "moment/moment";
import {Subject} from "rxjs";
import {DateRange} from "../../common/date/date-range/date-range";
import {
  ChartSerie
} from "../../system-link/system-link-logged-in/service-monitoring/service-monitoring-chart/service-monitoring-chart.component";
import {
  SerieConfig
} from "../../system-link/system-link-logged-in/service-monitoring/shared-chart-legend/shared-chart-legend.component";
import {PieSeriesOption} from "echarts";
import {stringToRgba} from "../../system-link/system-link-logged-in/service-monitoring/chart-utils";

type TimeDefinitions = 'relative' | 'absolute';

@Component({
    selector: 'app-commercial-release-dashboard',
    templateUrl: './commercial-release-dashboard.component.html',
    styleUrls: ['./commercial-release-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommercialReleaseDashboardComponent implements AfterViewInit, OnDestroy {
  static dateTimeRange = new EventEmitter<DateTimeRange>();
  static zoomSubject = new EventEmitter<number>();
  static destroyNotifier = new Subject();

  zoomed: boolean;
  dateTimeRange: DateTimeRange;
  timeFilter: TimeDefinitions = 'relative';
  timeValue: number = 1;
  measurement: unitOfTime.Base = "days";
  measurements: any[] = Object.values(MeasurementTypes).filter((v) => isNaN(Number(v)));

  autoRefresh: boolean;
  currentRefreshTimeout: any;

  // Datasets
  serieConfig: SerieConfig[];
  private commercialReleases: CommercialReleasesResult = {};
  private releaseToParties: ReleaseToPartiesResult = {};
  commercialReleasesPerOrganisation: ChartSerie;
  releaseToPartiesPerOrganisation: ChartSerie;

  get getCurrentTimeRange(): string {
    return this.timeFilter === 'relative'
      ? `${this.timeValue} ${this.measurement} ago`
      : `${moment(this.dateTimeRange.start).format("DD-MMM-YYYY HH:mm:ss")}
      → ${moment(this.dateTimeRange.end).format("DD-MMM-YYYY HH:mm:ss")}`;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.loadFromLocalStorage();
      this.setDateRange();
      this.updateDashboard();
    }, 0);
  }

  ngOnDestroy(): void {
    CommercialReleaseDashboardComponent.destroyNotifier.next(null);
    CommercialReleaseDashboardComponent.destroyNotifier.complete();
    this.stopRefresh();
  }

  createCommercialReleaseDatasets(result: CommercialReleasesResult) {
    delete result["Hapag-Lloyd Rotterdam"];
    this.commercialReleases = result;
    this.createCategories();
    this.commercialReleasesPerOrganisation = {
      name: "Commercial releases per organisation",
      serie: <PieSeriesOption>{
        type: "pie",
        radius: [20, 140],
        center: ['50%', '55%'],
        roseType: 'radius',
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true
          }
        },
        data: Object.entries(this.commercialReleases).map(s => ({
          value: s[1],
          name: s[0],
          itemStyle: {
            color: stringToRgba(s[0])
          }
        }))
      }
    };
  }

  createReleaseToPartiesDatasets(result: ReleaseToPartiesResult) {
    this.releaseToParties = result;
    this.createCategories();
    this.releaseToPartiesPerOrganisation = {
      name: "Release to parties per organisation",
      serie: <PieSeriesOption>{
        type: "pie",
        radius: [20, 140],
        center: ['50%', '55%'],
        roseType: 'radius',
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true
          }
        },
        data: Object.entries(this.releaseToParties).map(s => ({
          value: s[1],
          name: s[0],
          itemStyle: {
            color: stringToRgba(s[0])
          }
        }))
      }
    };
  }

  private createCategories() {
    const keys = new Set<string>((this.releaseToParties ? Object.keys(this.releaseToParties) : [])
      .concat((this.commercialReleases ? Object.keys(this.commercialReleases) : [])));
    this.serieConfig = [...keys].map(e => ({
      name: e,
      selected: true,
      color: stringToRgba(e)
    }));
  }

  resetZoom() {
    this.zoomed = false;
    this.setDateRange();
  }

  onRefreshButtonClick = () => {
    if (this.currentRefreshTimeout) {
      this.stopRefresh();
    } else {
      this.updateDashboard();
    }
  }

  private updateDashboard = () => {
    sendQuery("com.portbase.bezoekschip.common.api.commercialrelease.stats.GetCommercialReleaseStats",
      <GetCommercialReleaseStats>{
        dateTimeRange: this.getDataPayloadDateRange()
      }, {
        caching: true
      }).subscribe((result: CommercialReleasesResult) => {
      this.createCommercialReleaseDatasets(result);
    });
    sendQuery("com.portbase.bezoekschip.common.api.commercialrelease.stats.GetReleaseToPartyStats",
      <GetReleaseToPartyStats>{
        dateTimeRange: this.getDataPayloadDateRange()
      }, {
        caching: true
      }).subscribe((result: ReleaseToPartiesResult) => {
      this.createReleaseToPartiesDatasets(result);
    });
  }

  refreshOverTime = (): void => {
    this.autoRefresh = true;
    this.updateDashboard();
    this.startRefresh();
  }

  onSelectionChange = (): void => {
    this.setLocalStorage();
    this.resetZoom();
    this.updateDashboard();
  };

  measurementSelected = (value: unitOfTime.Base) => {
    if (value) {
      this.measurement = value;
      this.timeFilter = 'relative';
      this.onSelectionChange();
    }
  }

  onTimeValueChange = (timeValue: number) => {
    if (timeValue) {
      this.timeValue = timeValue;
      this.timeFilter = 'relative';
      this.onSelectionChange();
    }
  }

  onDateSelection = (dateRange: DateRange): void => {
    if (dateRange) {
      this.dateTimeRange = dateRange;
      this.timeFilter = 'absolute';
      this.stopRefresh();
      this.onSelectionChange();
    }
  }

  private setDateRange() {
    this.dateTimeRange = this.timeFilter === "relative" ? {
      start: moment().subtract(this.timeValue, this.measurement).toISOString(),
      end: moment().toISOString()
    } : this.dateTimeRange;
    CommercialReleaseDashboardComponent.dateTimeRange.next(this.dateTimeRange);
  };

  private getDataPayloadDateRange = (): DateTimeRange => ({
    start: moment(this.dateTimeRange.start).toISOString(),
    end: moment(this.dateTimeRange.end).toISOString()
  })

  private startRefresh = (): void => {
    if (this.currentRefreshTimeout) {
      clearTimeout(this.currentRefreshTimeout);
      this.currentRefreshTimeout = null;
    }
    this.currentRefreshTimeout = setTimeout(() => {
      if (this.currentRefreshTimeout) {
        this.setDateRange();
        this.updateDashboard();
        this.startRefresh();
      }
    }, 900000);
  }

  private stopRefresh() {
    if (this.currentRefreshTimeout) {
      clearTimeout(this.currentRefreshTimeout);
      this.currentRefreshTimeout = null;
    }
    this.autoRefresh = false;
  }

  private setLocalStorage() {
    localStorage.setItem('commercial-release-dashboard-days-preference', JSON.stringify(<LocalStorageData>{
      timeValue: this.timeValue,
      measurement: this.measurement,
      timeFilter: this.timeFilter,
      range: this.dateTimeRange
    }));
  }

  private loadFromLocalStorage(): void {
    const item = localStorage.getItem('commercial-release-dashboard-days-preference');
    if (item) {
      const preference: LocalStorageData = JSON.parse(item);
      this.timeValue = preference.timeValue;
      this.measurement = preference.measurement;
      this.timeFilter = preference.timeFilter;
      this.dateTimeRange = preference.range;
    }
  }

  get totalNumberOfCommercialReleases(): number {
    return lodash.sum(Object.values(this.commercialReleases));
  }

  get totalNumberOfReleaseToParties(): number {
    return lodash.sum(Object.values(this.releaseToParties));
  }
}

interface LocalStorageData {
  timeValue: number;
  range: DateTimeRange;
  measurement: unitOfTime.Base;
  timeFilter: TimeDefinitions;
}

enum MeasurementTypes {
  days = 86400,
  weeks = 604800,
  months = 2592000,
  years = 31536000,
}

interface CommercialReleasesResult {
  [organisation: string]: number;
}

interface ReleaseToPartiesResult {
  [organisation: string]: number;
}
